import { io, type Socket } from 'socket.io-client';
import type { Ack, ClientToServerEvents, ServerToClientEvents } from '@secretnenshi/shared';

export type GameSocket = Socket<ServerToClientEvents, ClientToServerEvents>;

let socket: GameSocket | null = null;

/** Same-origin by default; tests pass the server URL. */
export function initSocket(url?: string): GameSocket {
  socket?.disconnect();
  socket = (url ? io(url, { autoConnect: false }) : io({ autoConnect: false })) as GameSocket;
  return socket;
}

export function getSocket(): GameSocket {
  if (!socket) throw new Error('Socket not initialised');
  return socket;
}

export class RequestError extends Error {
  constructor(
    readonly code: string,
    message: string,
  ) {
    super(message);
  }
}

type Payload<E extends keyof ClientToServerEvents> = Parameters<ClientToServerEvents[E]>[0];
type Result<E extends keyof ClientToServerEvents> = Parameters<ClientToServerEvents[E]>[1] extends (r: Ack<infer T>) => void ? T : never;

const TIMEOUT_MS = 10_000;

/** Emit and wait for the server's acknowledgement. Rejects with RequestError. */
export function request<E extends keyof ClientToServerEvents>(event: E, payload: Payload<E>): Promise<Result<E>> {
  const s = getSocket();
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new RequestError('TIMEOUT', 'The server did not respond. Check your connection.')), TIMEOUT_MS);
    (s.emit as (e: string, p: unknown, ack: (r: Ack<unknown>) => void) => void)(event, payload, (res) => {
      clearTimeout(timer);
      if (res.ok) resolve(res.data as Result<E>);
      else reject(new RequestError(res.error.code, res.error.message));
    });
  });
}
